/**
 * Voice tab — MEMBER-self-serve TTS toggle + voice picker for the
 * user's spoken-response output. Per handoff §3:
 *
 *   - Reads `data.user.voice.tts` + `data.org.voice.allowedVoices`.
 *   - Writes `PATCH /api/lx/openclaw/config/user` body
 *     `{ voice: { tts: { enabled, selectedVoice } } }`.
 *   - The voice slate is org-curated: the user picks from
 *     `allowedVoices[]` only; the runtime cross-validates server-side
 *     and rejects with 409 VOICE_NOT_ALLOWED on a stale pick.
 *
 * Slice 2 ships the toggle + picker. The per-voice "Preview" button
 * mentioned in the handoff requires the existing `tts.convert` surface
 * to be exposed via the `/api/lx/openclaw/*` rail; that's a follow-up
 * sub-slice (likely paired with the openclaw plugin's tts adapter).
 *
 * Lockable surfaces:
 *   - `userSettingsPolicy.voice === false` → entire tab read-only,
 *     "locked by org" tooltip.
 *   - Empty `org.voice.allowedVoices` → friendly explainer + disabled
 *     picker; toggle stays editable so the user can opt out cleanly.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Mic, Save, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { useToastStore } from "@/stores/useToastStore";
import {
  getOpenclawConfig,
  patchOpenclawUserConfig,
  LxOpenclawConfigError,
  type OpenclawConfig,
} from "@/api/lx-openclaw-config";
import { isDisabledByOrg } from "@/lib/openclaw-policy";
import { cn } from "@/lib/utils";
import { useOpenclawSettingsOptional } from "./OpenclawSettingsTabs";

export function VoiceControls({ chrome = true }: { chrome?: boolean }) {
  const { t } = useTranslation();
  // Optional context: inside the settings shell we reuse its loaded config +
  // refresh; in the hero popover (S6) there is no shell, so we load our own
  // and refresh it ourselves. Same decoupling `TelegramSetupWizard` uses.
  const shell = useOpenclawSettingsOptional();
  const [ownConfig, setOwnConfig] = useState<OpenclawConfig | null>(null);
  const loadOwn = useCallback(() => {
    if (shell) return Promise.resolve();
    return getOpenclawConfig()
      .then((c) => setOwnConfig(c))
      .catch(() => undefined);
  }, [shell]);
  useEffect(() => {
    void loadOwn();
  }, [loadOwn]);
  const config = shell?.config ?? ownConfig;
  const refresh = shell?.refresh ?? loadOwn;
  const notifyRouteNotFound = shell?.notifyRouteNotFound ?? (() => undefined);
  if (!config) return null;
  return (
    <VoiceControlsInner
      config={config}
      refresh={refresh}
      notifyRouteNotFound={notifyRouteNotFound}
      chrome={chrome}
    />
  );
}

function VoiceControlsInner({
  config,
  refresh,
  notifyRouteNotFound,
  chrome,
}: {
  config: OpenclawConfig;
  refresh: () => Promise<void> | void;
  notifyRouteNotFound: (tab: "voice") => void;
  chrome: boolean;
}) {
  const { t } = useTranslation();
  const addToast = useToastStore((s) => s.addToast);

  const voiceLockedByOrg = isDisabledByOrg(config.org, "voice");
  const allowedVoices = config.org.voice?.allowedVoices ?? [];
  const serverEnabled = config.user?.voice?.tts?.enabled ?? false;
  const serverSelected = config.user?.voice?.tts?.selectedVoice ?? "";

  // Local edit buffer. Reset whenever the canonical server view flips
  // (after a successful save, after a `refresh()` from another sibling
  // tab, or after an org-side allowedVoices change drops our selection).
  const [enabled, setEnabled] = useState(serverEnabled);
  const [selected, setSelected] = useState(serverSelected);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    setEnabled(serverEnabled);
    setSelected(serverSelected);
  }, [serverEnabled, serverSelected]);

  // If the org just retracted the currently-selected voice from its
  // slate, the local draft becomes stale. Don't auto-flip it (would
  // silently rewrite the user's choice); instead surface the conflict
  // by greying the chip set and showing the "voiceNotAllowed" hint on
  // save attempt.
  const selectionStillAllowed =
    selected === "" || allowedVoices.includes(selected);

  const dirty = enabled !== serverEnabled || selected !== serverSelected;
  const canSave = dirty && !voiceLockedByOrg && !submitting;

  async function onSave() {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await patchOpenclawUserConfig({
        voice: {
          tts: {
            enabled,
            // Only send selectedVoice when one is picked AND it's still
            // in the org slate. Omitting the field falls through the
            // runtime's deep-merge unchanged (preserves the server's
            // existing value). If the local draft was cleared OR the
            // org retracted our pick, send no selectedVoice — the user
            // will revisit the picker after the policy refresh below.
            ...(selected && selectionStillAllowed
              ? { selectedVoice: selected }
              : {}),
          },
        },
      });
      await refresh();
      addToast({
        kind: "info",
        text: t("settings.openclaw.voice.saved"),
        autoDismissMs: 4000,
      });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function handleSaveError(err: unknown) {
    if (err instanceof LxOpenclawConfigError) {
      if (err.routeNotFound) {
        notifyRouteNotFound("voice");
        addToast({
          kind: "error",
          text: t("settings.openclaw.upgradeRequired.toast"),
          autoDismissMs: 8000,
        });
        return;
      }
      if (err.code === "USER_SETTING_DISABLED_BY_ORG") {
        void refresh();
        addToast({
          kind: "error",
          text: t("settings.openclaw.disabledByOrgRace"),
          autoDismissMs: 6000,
        });
        return;
      }
      if (err.code === "VOICE_NOT_ALLOWED") {
        // Org retracted the voice between focus + submit. Pull the
        // latest slate so the chips reflect what's actually pickable.
        void refresh();
        addToast({
          kind: "error",
          text: t("settings.openclaw.voice.voiceNotAllowed"),
          autoDismissMs: 6000,
        });
        return;
      }
    }
    addToast({
      kind: "error",
      text: t("settings.openclaw.saveFailed"),
      autoDismissMs: 6000,
    });
  }

  const inner = (
    <>
      <div className={cn("flex items-center gap-2", !chrome && "hidden")}>
        <Mic size={18} className="text-cream-500" />
        <h3 className="font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
          {t("settings.openclaw.voice.title")}
        </h3>
        {voiceLockedByOrg && (
          <span
            data-testid="openclaw-voice-locked"
            title={t("settings.openclaw.lockedByOrgTooltip")}
            className="inline-flex items-center gap-1 rounded bg-cream-100 px-1.5 py-0.5 text-[10px] font-medium text-coffee-500 dark:bg-coffee-800 dark:text-cream-400"
          >
            <Lock size={9} />
            {t("settings.openclaw.lockedByOrg")}
          </span>
        )}
      </div>
      <p className={cn("mt-3 text-body-sm text-coffee-600 dark:text-cream-400", !chrome && "hidden")}>
        {t("settings.openclaw.voice.caption")}
      </p>

      <div className="mt-5 flex flex-col gap-4">
        {/* Enable toggle — kept as a labelled checkbox rather than a
            stylized switch so it ships without a new primitive. */}
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            data-testid="openclaw-voice-enabled"
            checked={enabled}
            disabled={voiceLockedByOrg || submitting}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-1 h-4 w-4 cursor-pointer accent-gold-500 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="flex flex-col">
            <span className="text-body-sm font-medium text-coffee-700 dark:text-cream-300">
              {t("settings.openclaw.voice.enabledLabel")}
            </span>
            <span className="text-caption text-coffee-500 dark:text-cream-500">
              {t("settings.openclaw.voice.enabledHint")}
            </span>
          </span>
        </label>

        {/* Voice picker — chips. Disabled (greyed) when:
              - org locks the whole voice surface,
              - TTS is disabled (selecting a voice with TTS off would be
                a silent no-op when the agent actually speaks),
              - the org slate is empty.
            The "no allowed voices" hint replaces the chip strip when the
            slate is empty AND the user hasn't already picked one
            (preserves their stale selection for context). */}
        <div className="flex flex-col gap-2">
          <p className="text-body-sm font-medium text-coffee-700 dark:text-cream-300">
            {t("settings.openclaw.voice.voicesLabel")}
          </p>
          {allowedVoices.length === 0 ? (
            <p
              data-testid="openclaw-voice-no-allowed"
              className="text-caption text-coffee-500 dark:text-cream-500"
            >
              {t("settings.openclaw.voice.noAllowedVoices")}
            </p>
          ) : (
            <div
              role="radiogroup"
              aria-label={t("settings.openclaw.voice.voicesLabel")}
              className={cn(
                "flex flex-wrap gap-2",
                (voiceLockedByOrg || !enabled || submitting) &&
                  "pointer-events-none opacity-60",
              )}
            >
              {allowedVoices.map((voice) => {
                const isSelected = selected === voice;
                return (
                  <Chip
                    key={voice}
                    role="radio"
                    aria-checked={isSelected}
                    active={isSelected}
                    data-testid={`openclaw-voice-pick-${voice}`}
                    onClick={() => setSelected(voice)}
                  >
                    {voice}
                  </Chip>
                );
              })}
            </div>
          )}
          {/* Surface stale selection (org retracted the voice from its
              slate after we picked it). Keeps the user informed without
              auto-flipping their choice. */}
          {selected && !selectionStillAllowed && (
            <p
              data-testid="openclaw-voice-stale-selection"
              className="text-caption text-red-600 dark:text-red-400"
            >
              {t("settings.openclaw.voice.staleSelection", { voice: selected })}
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="openclaw-voice-save"
          disabled={!canSave}
          onClick={() => void onSave()}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-body-sm font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
            canSave
              ? "border-coffee-800 bg-coffee-800 text-cream-50 hover:bg-coffee-900 hover:border-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-50 dark:hover:border-cream-50"
              : "cursor-not-allowed border-cream-200/60 text-cream-500 dark:border-coffee-700/60",
          )}
        >
          {submitting ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Save size={13} />
          )}
          {submitting
            ? t("settings.openclaw.savingButton")
            : t("settings.openclaw.saveButton")}
        </button>
      </div>
    </>
  );

  return chrome ? (
    <Card variant="surface" data-testid="openclaw-voice-tab">
      {inner}
    </Card>
  ) : (
    <div data-testid="voice-controls">{inner}</div>
  );
}
